import logger from 'ws-scripts/modules/logger';

/**
 * Fires a 'modulesRefresh' event to initialize DDC modules against newly
 * injected DOM nodes. Uses the jQuery path when jQuery is available on the
 * page (required to reach the CMS default.js handler which is registered
 * as a jQuery listener). Falls back to a native CustomEvent for environments
 * without jQuery.
 *
 * @param {Record<string, object>|null} [modules] - a subset of DDC modules to
 *   refresh, keyed by module name (e.g. `{ lazyImage: window.DDC.modules.lazyImage }`),
 *   or omitted/null to refresh all modules. Never pass `window.DDC.modules`
 *   itself here — the CMS handler falls back to the full `DDC.modules`
 *   registry whenever `data.modules` is absent, so passing the whole
 *   registry as "modules" is indistinguishable from passing nothing.
 *
 *   A full/unscoped refresh is required after injecting fragment content,
 *   since a fragment's markup can contain any DDC module's selector (map,
 *   carousel, etc.) and that module's init logic already lives on the host
 *   page — it just needs a refresh to notice the new DOM. The trade-off:
 *   the CMS handler iterates modules with a plain $.each and does not
 *   isolate one module's failure from the next, so a module whose
 *   loadConditional throws (e.g. map-dynamic, if its jquery.inview
 *   dependency hasn't loaded yet) can abort the whole iteration. The
 *   try/catch below only prevents that from surfacing as an uncaught
 *   exception in our own call stack — it can't stop that module from
 *   blocking whichever other modules were still left in the loop.
 */
const fireModulesRefresh = (modules) => {
	if (typeof window === 'undefined') return;

	try {
		if (window.jQuery) {
			window
				.jQuery(document)
				.trigger('modulesRefresh', modules ? { modules } : undefined);
		} else {
			document.dispatchEvent(
				new CustomEvent('modulesRefresh', {
					bubbles: true,
					detail: modules ? { modules } : undefined
				})
			);
		}
	} catch (err) {
		logger.warn('[fireModulesRefresh] a DDC module handler threw:', err);
	}
};

export default fireModulesRefresh;
